import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { __ } from '../../helpers/TranslationService';
import { IPersonalData } from '../../interfaces/IRequestForm';
import { PersonalDataActionCreators } from '../../reducers/requestForm/action-creators';
import { IControl, useControl } from '../../hooks/useControl';
import useFlights from '../../hooks/useFlights';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { ValidatorsEnum } from '../../hooks/useValidation';
import { updateRequestFormFields } from '../../consts/actionTypes';

export enum FieldsEnumKeys {
    childAges = 'childAges',
    selectedAirportIndex = 'selectedAirportCode',
}

export interface IField {
    name: string;
    control: IControl;
    active: boolean;
}
export interface controlsEnum {
    [key: string]: IField;
}

export const useInquiryFormValidation = ({
    validateNow = false,
    isAllInclusiveSelected,
}: {
    validateNow?: boolean;
    isAllInclusiveSelected?: boolean;
}): controlsEnum => {
    const { pageData, requestForm, cruisePage } = useTypedSelector((state) => state);

    const airports = useFlights({
        includesFlight: cruisePage.includesFlight,
        cabins: cruisePage.cabins,
        selectedCabin: cruisePage.selectedCabin,
        rates: cruisePage.rates,
        currency: pageData.appConfig.defaultCurrency,
        cabinNid: requestForm.cabinNid,
        cruisePageAirports: cruisePage.airports,
        selectedSail: cruisePage.selectedSailIndex,
        sailNid: requestForm.sailNid,
        passengersNum: requestForm?.passengers?.length,
        isAllInclusive: isAllInclusiveSelected,
    });

    const personalRequestForm: IPersonalData = Object.values(FieldsEnumKeys).reduce(
        (accumulator, field) => ({
            ...accumulator,
            [field]: requestForm[field] !== undefined ? requestForm[field] : null,
        }),
        {},
    ) as IPersonalData;
    const isFligths: boolean = airports.isFligths;
    const showAirportSelect: boolean = !!cruisePage.includesFlight;

    const controlsEnum: controlsEnum = {
        [FieldsEnumKeys.childAges]: {
            name: FieldsEnumKeys.childAges,
            control: useControl({
                initialValue: personalRequestForm[FieldsEnumKeys.childAges],
                validators: [ValidatorsEnum.isChildAgesValid],
                validateNow,
            }),
            active: true,
        },
        [FieldsEnumKeys.selectedAirportIndex]: {
            name: FieldsEnumKeys.selectedAirportIndex,
            control: useControl({
                initialValue: personalRequestForm[FieldsEnumKeys.selectedAirportIndex],
                validators: isFligths
                    ? [ValidatorsEnum.isFlightsEmpty]
                    : [ValidatorsEnum.isAirportEmpty],
                options: airports.options,
                validateNow,
            }),
            active: showAirportSelect,
        },
    };

    const isFormValid: boolean = controlsEnum
        ? Object.values(controlsEnum).every((c) => (c.control.isValid && c.active) || !c.active)
        : false;
    const dispatch = useDispatch();
    const getNewPersonalData = () => {
        const newPersonalData = Object.entries(controlsEnum).reduce(
            (acc, [key, item]) => ({
                ...acc,
                [key]: !item.active ? requestForm[key] : item.control.value,
            }),
            {},
        );
        return newPersonalData;
    };
    const isEqual = (...objects) =>
        objects.every((obj) => JSON.stringify(obj) === JSON.stringify(objects[0]));
    const [newPersonalData, setNewPersonalData] = useState(getNewPersonalData());
    useEffect(() => {
        if (!isFormValid) {
            return;
        }
        const updatedPersonalData = getNewPersonalData();
        if (isEqual(updatedPersonalData, newPersonalData)) {
            return;
        }
        setNewPersonalData(updatedPersonalData);
    }, [controlsEnum]);

    useEffect(() => {
        if (isFormValid) {
            dispatch(PersonalDataActionCreators.setPersonalData(newPersonalData));
        }
    }, [newPersonalData]);

    useEffect(() => {
        // requestForm.includesFlight is never updated and stays false even if there is a flight included
        // isRequestFormValid() function relies on this field to validate form before sending
        // so here we want it to be in sync
        dispatch({
            type: updateRequestFormFields,
            payload: { includesFlight: showAirportSelect },
        });
    }, [showAirportSelect]);

    return controlsEnum;
};
